import React, { useMemo } from 'react';
import AntLayout from 'antd/es/layout';
import { Flex, Popconfirm, Skeleton, notification, theme } from 'antd';
import styles from './footer.module.css';
import { Button, LexicalEditor } from '@seaters/ui';
import ReactDOM from 'react-dom';
import {
  SeatDistributionMode,
  SeatStatus,
  WLPositionStatus,
  WaitingListEntity,
  WaitingListStatusEnum,
  routes,
} from '@seaters/constants';
import { LockOutlined, StarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  getMapTranslation,
  getSlugFromUrl,
  useFanCancelWaitingList,
  useFetchLook,
} from '@seaters/data-access';

const { Footer: AntFooter } = AntLayout;

export function RequestTicketsFooter({
  openRequestTickets,
  waitingList,
  isLoading = false,
  editGuestList,
  setStartRefetching,
}: {
  openRequestTickets: () => void;
  waitingList?: WaitingListEntity;
  isLoading?: boolean;
  editGuestList?: boolean;
  setStartRefetching: (v: 'cancel') => void;
}) {
  const { token } = theme.useToken();
  const { t, ready, i18n } = useTranslation();
  const { language } = i18n;
  const lang = language === 'nl_NL' || language === 'nl_BE' ? 'nl' : language;
  const navigate = useNavigate();

  const slug = getSlugFromUrl();
  const { data: lookData } = useFetchLook(slug ?? '');

  const waitingListId = waitingList?.waitingListId ?? '';

  const isComingSoonWL =
    waitingList?.waitingListStatus === WaitingListStatusEnum.PUBLISHED;

  const { mutate: fanCancelWaitingList } =
    useFanCancelWaitingList(waitingListId);

  const rejectWaitingList = () => {
    fanCancelWaitingList(
      { waitingListId },
      {
        onSuccess: () => {
          setStartRefetching('cancel');
          notification.success({
            message: t('booking_cancellation_notification_success'),
          });
          // setIsActive(false);
        },
        onError: (err) => {
          console.error(err);
          notification.error({
            message: t('booking_cancellation_notification_error'),
          });
        },
      }
    );
  };

  const title = useMemo(() => {
    if (!waitingList) return undefined;

    if (editGuestList) return t('view_guestlist');

    if (!waitingList.position) return t('queue_header_join-btn');

    // Prefer accepted state over requested label
    if (
      waitingList?.seat?.status === SeatStatus.ACCEPTED ||
      waitingList?.seat?.status === SeatStatus.RSVP_ACCEPTED
    ) {
      return t('download_tickets_button_text');
    }

    if (waitingList?.position?.status === WLPositionStatus.WAITING_SEAT) {
      return t('tickets_requested_button_text');
    }

    return t('accept_tickets_button_text');
  }, [waitingList, ready, editGuestList, t]);

  const isAbleToLeaveWL =
    waitingList?.waitingListStatus === WaitingListStatusEnum.OPEN &&
    waitingList?.seatDistributionMode === SeatDistributionMode.VOUCHER &&
    waitingList?.freeWaitingList &&
    lookData?.properties?.directSaleModeTicketsLimit;

  return ReactDOM.createPortal(
    <AntFooter
      className={styles.requestTicketsFooter}
      style={{
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        padding: '0',
      }}
    >
      {isLoading ? (
        <Skeleton.Button size="large" block />
      ) : (
        <Flex vertical className="w-full">
          {isAbleToLeaveWL && (
            <Popconfirm
              title={t('wishlist_leave_wishlist_modal_title')}
              onConfirm={rejectWaitingList}
              description={
                <LexicalEditor
                  borderless
                  compact
                  disabled
                  placeholder=""
                  toolbar={false}
                  value={String(
                    t('wishlist_leave_wishlist_modal_message', {
                      wishlistname:
                        waitingList?.translatedEventName ??
                        (getMapTranslation(waitingList?.experienceName, lang) ||
                          getMapTranslation(waitingList?.eventName, lang)),
                    })
                  )}
                />
              }
              cancelText={t('general_no')}
            >
              <Button
                data-testid="leaveWaitingListButton"
                type="link"
                color={token.colorTextBase}
                style={{
                  padding: '12px 0',
                  height: 'unset',
                }}
              >
                {t('leave_wl_with_tickets_button_text')}
              </Button>
            </Popconfirm>
          )}
          <Button
            block
            style={{ borderRadius: '0px', padding: '12px 0', height: 'unset' }}
            type="primary"
            onClick={
              editGuestList
                ? () => navigate(routes.guestlist)
                : openRequestTickets
            }
            disabled={isComingSoonWL}
            className="py-3"
            data-testid="requestTicketsButton"
            data-coming-soon={isComingSoonWL}
            icon={isComingSoonWL && <LockOutlined rev={undefined} />}
          >
            {isComingSoonWL ? t('group_waitinglists_comingsoon-label') : title}
          </Button>
        </Flex>
      )}
    </AntFooter>,
    document.getElementById('portalForFooter') as HTMLElement
  );
}

export default RequestTicketsFooter;
